import type { ProjectPrebuildConfiguration } from "gitpod-next-api/gitpod/v1/project_pb";
import type { PlainMessage } from "@bufbuild/protobuf";

/**
 * Calculates the schedule message for the prebuild empty state
 * @param prebuildConfig - The project's prebuild configuration
 * @returns A message describing when the next prebuild will run or that prebuilds are disabled
 */
export const getPrebuildScheduleMessage = (
    prebuildConfig: PlainMessage<ProjectPrebuildConfiguration> | undefined,
): string => {
    const prebuildEnabled = prebuildConfig?.enabled || false;
    const scheduledHourUtc =
        prebuildConfig?.trigger?.trigger?.case === "dailySchedule"
            ? prebuildConfig.trigger.trigger.value.hourUtc
            : undefined;

    if (!prebuildEnabled) {
        return "Prebuilds are currently disabled. You can enable them in the project settings.";
    }

    if (scheduledHourUtc === undefined) {
        return "Prebuilds are currently disabled. You can enable them in the project settings.";
    }

    const now = new Date();
    const nextRun = new Date();
    nextRun.setUTCHours(scheduledHourUtc, 0, 0, 0);

    // If the scheduled time has passed today, schedule for tomorrow
    if (nextRun <= now) {
        nextRun.setUTCDate(nextRun.getUTCDate() + 1);
    }

    const isToday =
        nextRun.getUTCDate() === now.getUTCDate() &&
        nextRun.getUTCMonth() === now.getUTCMonth() &&
        nextRun.getUTCFullYear() === now.getUTCFullYear();

    const timeStr = `${scheduledHourUtc.toString().padStart(2, "0")}:00`;
    return `The first run is scheduled for ${isToday ? "today" : "tomorrow"} at ${timeStr} UTC.`;
};
